import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PANELS = [
  { id: 'freight', kind: 'steps', to: '/quote' },
  { id: 'partner', kind: 'steps', to: '/contact' },
  { id: 'understand', kind: 'points', to: '#coordination-flow' },
];

const VisitorSelector = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState('freight');
  const tabRefs = useRef({});

  const focusTab = (id) => {
    setActive(id);
    const node = tabRefs.current[id];
    if (node) node.focus();
  };

  const onKeyDown = (event) => {
    const index = PANELS.findIndex((panel) => panel.id === active);
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusTab(PANELS[(index + 1) % PANELS.length].id);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusTab(PANELS[(index - 1 + PANELS.length) % PANELS.length].id);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTab(PANELS[0].id);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTab(PANELS[PANELS.length - 1].id);
    }
  };

  const renderList = (panel) => {
    const items = t(`homePage.visitorSelector.${panel.id}.${panel.kind}`, { returnObjects: true });
    if (!Array.isArray(items)) return null;

    if (panel.kind === 'steps') {
      return (
        <ol className="space-y-4">
          {items.map((item, index) => (
            <li key={`${panel.id}-step-${index}`} className="flex items-start gap-4">
              <span className="w-8 h-8 rounded-full bg-gold/15 text-navy-900 font-space font-bold text-sm flex items-center justify-center flex-shrink-0">
                {index + 1}
              </span>
              <span className="text-slate-700 leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
      );
    }

    return (
      <ul className="space-y-4">
        {items.map((item, index) => (
          <li key={`${panel.id}-point-${index}`} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span className="text-slate-700 leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="py-20 md:py-28 bg-offwhite" data-testid="visitor-selector-section">
      <div className="container-custom">
        <div className="max-w-3xl mb-12">
          <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
            {t('homePage.visitorSelector.title')}
          </h2>
          <p className="text-lg text-slate-600">{t('homePage.visitorSelector.intro')}</p>
        </div>

        <div
          role="tablist"
          aria-label={t('homePage.visitorSelector.title')}
          onKeyDown={onKeyDown}
          className="grid md:grid-cols-3 gap-4 mb-8"
        >
          {PANELS.map((panel) => {
            const isActive = panel.id === active;
            return (
              <button
                key={panel.id}
                id={`visitor-tab-${panel.id}`}
                ref={(node) => {
                  tabRefs.current[panel.id] = node;
                }}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-controls={`visitor-panel-${panel.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActive(panel.id)}
                className={`text-left rounded-xl border-2 p-5 font-space font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 ${
                  isActive
                    ? 'border-gold bg-white text-navy-900 shadow-sm'
                    : 'border-platinum-light bg-white/60 text-slate-600 hover:border-gold/50'
                }`}
              >
                {t(`homePage.visitorSelector.${panel.id}.label`)}
              </button>
            );
          })}
        </div>

        {PANELS.map((panel) => (
          <div
            key={panel.id}
            id={`visitor-panel-${panel.id}`}
            role="tabpanel"
            aria-labelledby={`visitor-tab-${panel.id}`}
            hidden={panel.id !== active}
            tabIndex={0}
            className="rounded-2xl border border-platinum-light bg-white p-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            <h3 className="font-space text-xl font-bold text-navy-900 mb-6">
              {t(`homePage.visitorSelector.${panel.id}.title`)}
            </h3>
            {renderList(panel)}
            <div className="mt-8">
              {panel.to.startsWith('#') ? (
                <a href={panel.to}>
                  <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 px-6 py-4 font-semibold">
                    {t(`homePage.visitorSelector.${panel.id}.cta`)}
                    <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                  </Button>
                </a>
              ) : (
                <Link to={panel.to}>
                  <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 px-6 py-4 font-semibold">
                    {t(`homePage.visitorSelector.${panel.id}.cta`)}
                    <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default VisitorSelector;
